/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useLayoutEffect, useEffect, useMemo, useCallback } from 'react';
import { useMutation } from 'react-query';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import PointsTable from 'components/Tables/Point/PointsTable/PointsTable';
import { AppLoader } from 'components/Core/AppLoader';
import { Button } from 'components/Core/Button';
import SelectComponent from 'components/Inputs/SelectComponent';
import AutocompleteSelect from 'components/Inputs/AutocompleteSelect';
import { CreatedBySelect } from 'components/Inputs/CreatedBySelect';
import { ControlledInput } from 'components/Inputs/ControlledInput';
import EntityOption from 'components/Core/EntityOption';
import PointCategoryItem from 'components/Core/PointCategoryItem';
import { availableRoles, DEFAULT_SELECT_STYLES, FIRST_ROW_SELECT_STYLES, validationStatusOptions } from './constants';
import { IBrandRef } from 'types/common';
import { IPointsCategoryData } from 'stores/points/types';
import { pointsTranslations } from './translations';
import { IPointsFilters, PointsTransFields } from './types';
import styles from './points.module.scss';
import IsDeletedSelect from 'components/Inputs/IsDeletedSelect/IsDeletedSelect';

const Points: FC = observer(() => {
  const {
    pointsStore: { getPointsData, pointsQuery, clearData, getPointsCategories, pointsCategories },
    brandsStore: { getAutoCompleteBrands },
    tableStore: { startPageIndex },
    mainStore: { user },
  } = useStore();

  const { t } = useMyTranslations<PointsTransFields>([pointsTranslations.name]);

  const navigate = useNavigate();

  useLayoutEffect(() => {
    clearData();
  }, []);

  const { isLoading: isLoadingCategories, mutate: getCategories } = useMutation(getPointsCategories, {
    mutationKey: 'getPointsCategories',
    retry: false,
  });

  const { isLoading, mutate } = useMutation(getPointsData, {
    mutationKey: 'getDataPointsList',
    retry: false,
  });

  useEffect(() => {
    if (!startPageIndex) {
      mutate(pointsQuery);
      getCategories();
    }
  }, [user?.selectedCountryCode]);

  const useFormMethods = useForm<IPointsFilters>({
    defaultValues: {
      ...pointsQuery,
    },
  });
  const { handleSubmit } = useFormMethods;

  const renderBrandOption = useCallback((data: IBrandRef) => {
    return <EntityOption image={data.logoUrl} entityType='brands' name={data.name} />;
  }, []);

  const renderCategoriesOption = useCallback((data: IPointsCategoryData) => {
    return <PointCategoryItem category={data} />;
  }, []);

  const onSubmit = useCallback((data: IPointsFilters) => {
    mutate(data);
  }, []);

  const showBtn = useMemo(() => user && availableRoles.includes(user.role), [user?.role]);

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <h2 className={styles.headerTitle}>{t('pointsList')}</h2>
      </header>
      <FormProvider {...(useFormMethods as any)}>
        <div className={styles.filters}>
          <div className={styles.filtersBlock}>
            <div className={styles.filtersRow}>
              <AutocompleteSelect
                name='brand'
                placeholder={t(`brand`)}
                data={[]}
                optionLabel='name'
                optionValue='id'
                onSearch={getAutoCompleteBrands}
                customOption={renderBrandOption}
                customSingleValue={renderBrandOption}
                styleProps={FIRST_ROW_SELECT_STYLES}
              />
              <ControlledInput
                name='search'
                type='text'
                placeholder={t('search')}
                isRemoveMargin
                classNameProp={styles.searchInput}
              />
            </div>
            <div className={styles.filtersRow}>
              <SelectComponent
                name='category'
                placeholder={t('category')}
                options={pointsCategories}
                optionLabel='categoryName'
                optionValue='id'
                customOption={renderCategoriesOption}
                customSingleValue={renderCategoriesOption}
                styleProps={DEFAULT_SELECT_STYLES}
                isDisabled={isLoadingCategories}
              />
              <SelectComponent
                name='validationStatus'
                placeholder={t('validationStatus')}
                options={validationStatusOptions.map((item) => ({ ...item, name: t(item.name) }))}
                optionLabel='name'
                optionValue='id'
                styleProps={DEFAULT_SELECT_STYLES}
              />
              <IsDeletedSelect />
              <CreatedBySelect />
            </div>
          </div>
          <div className={styles.buttonsBlock}>
            <Button
              title={t('search')}
              onClick={handleSubmit(onSubmit)}
              classNameProp={`${styles.btn} ${styles.searchBtn}`}
              isDisabled={isLoading}
            />
            {showBtn && <Button title={t('addBtn')} onClick={() => navigate('add')} classNameProp={styles.btn} />}
          </div>
        </div>
      </FormProvider>
      {isLoading ? <AppLoader /> : <PointsTable />}
    </main>
  );
});

export default Points;
